import { abs, max, round } from 'mathjs'
import {
  GeckoComponentCoords,
  GeckoComponentDataFrame,
  GeckoComponentPath,
  GeckoComponentRect,
  GeckoHorizontalWeld,
  GeckoMarkupRect,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
  GeckoShellCourse,
  GeckoVerticalWeld,
  GeckoWeld,
} from 'types'

function undefinedTypeCheck(arr: (unknown | undefined)[]): boolean {
  for (const element of arr) {
    if (element === undefined) {
      return true
    }
  }
  return false
}

export const Side = ['x_start', 'x_end', 'y_start', 'y_end'] as const
export type Side = (typeof Side)[number]

export type BinBox = {
  x_start: number
  x_end: number
  y_start: number
  y_end: number
  perimeter?: number[][]
  ramID?: string
}

export function coordsToBinIndices(
  coords: GeckoComponentCoords,
  cdf: GeckoComponentDataFrame
): [x: number, y: number] {
  return [measureToXBinIndex(coords.x, cdf), measureToYBinIndex(coords.y, cdf)]
}

export function measureToXBinIndex(
  x: GeckoMeasurement,
  cdf: GeckoComponentDataFrame
) {
  return Math.round(
    measurementRatio(
      x
        .minus(cdf.data.binning.bin_size.width.scale(0.5))
        .minus(cdf.data.geometry.x.start),
      cdf.data.binning.bin_size.width
    )
  )
}

export function measureToYBinIndex(
  y: GeckoMeasurement,
  cdf: GeckoComponentDataFrame
) {
  return Math.round(
    measurementRatio(
      y
        .minus(cdf.data.binning.bin_size.height.scale(0.5))
        .minus(cdf.data.geometry.y.start),
      cdf.data.binning.bin_size.height
    )
  )
}

export function binIndicesToCoord(
  xIndex: number,
  yIndex: number,
  cdf: GeckoComponentDataFrame
): GeckoComponentCoords {
  return GeckoComponentCoords.CreateFromMeasurements(
    cdf.tags.component,
    xBinIndexToMeasure(xIndex, cdf),
    yBinIndexToMeasure(yIndex, cdf)
  )
}

export function xBinIndexToMeasure(
  xIndex: number,
  cdf: GeckoComponentDataFrame
) {
  return cdf.data.binning.bin_size.width
    .scale(xIndex)
    .plus(cdf.data.geometry.x.start)
    .plus(cdf.data.binning.bin_size.width.scale(0.5))
}

export function yBinIndexToMeasure(
  yIndex: number,
  cdf: GeckoComponentDataFrame
) {
  return cdf.data.binning.bin_size.height
    .scale(yIndex)
    .plus(cdf.data.geometry.y.start)
    .plus(cdf.data.binning.bin_size.height.scale(0.5))
}

export function measurementRatio(a: GeckoMeasurement, b: GeckoMeasurement) {
  return a.num / b.convertTo(a.unit).num
}

export function binBoxToRect(box: BinBox, cdf: GeckoComponentDataFrame) {
  return GeckoComponentRect.CreateFromCoords(
    binIndicesToCoord(box.x_start, box.y_start, cdf),
    binIndicesToCoord(box.x_end, box.y_end, cdf)
  )
}

export function binBoxToPath(box: BinBox, cdf: GeckoComponentDataFrame) {
  return GeckoComponentPath.CreateFromCoords(
    box.perimeter?.map(([y, x]) => binIndicesToCoord(y, x, cdf)) ?? []
  )
}

export function gcrToBinBox(
  rect: GeckoComponentRect,
  cdf: GeckoComponentDataFrame
): BinBox {
  return {
    x_start: measureToXBinIndex(rect.start.x, cdf),
    x_end: measureToXBinIndex(rect.end.x, cdf),
    y_start: measureToYBinIndex(rect.start.y, cdf),
    y_end: measureToYBinIndex(rect.end.y, cdf),
  }
}

export function gmrToBinBox(
  rect: GeckoMarkupRect,
  cdf: GeckoComponentDataFrame
) {
  return {
    x_start: measureToXBinIndex(rect.bounds.start.x, cdf),
    x_end: measureToXBinIndex(rect.bounds.end.x, cdf),
    y_start: measureToYBinIndex(rect.bounds.start.y, cdf),
    y_end: measureToYBinIndex(rect.bounds.end.y, cdf),
  }
}

export function modifyGCRect(
  rect: GeckoComponentRect,
  side: Side,
  newVal: GeckoMeasurement
) {
  const rectJSON = rect.toJSON()
  const modifiedJSON = {
    ...rectJSON,
    [side]: newVal.convertTo(rect.x.unit).num,
  }
  return GeckoComponentRect.CreateFromJSON(
    rect.componentID,
    modifiedJSON
  ) as GeckoComponentRect //Should be safe
}

export function rectContainsRect(
  container: GeckoComponentRect,
  containee: GeckoComponentRect
) {
  const spoofedContainee = GeckoComponentRect.CreateFromJSON(
    container.componentID,
    containee.toJSON()
  ) as GeckoComponentRect
  return (
    GeckoComponentRect.Contains(container, spoofedContainee.start) &&
    GeckoComponentRect.Contains(container, spoofedContainee.end)
  )
}

export function rangeOverlaps(
  a: GeckoMeasurementRange,
  b: GeckoMeasurementRange
) {
  return (
    //Does A overlap B?)
    a.start.lte(b.end) && a.end.gte(b.start)
  )
}

export function filterHorizontalWelds(
  horizontalWelds: GeckoHorizontalWeld[],
  repairRects: GeckoComponentRect[]
) {
  if (repairRects.length === 0) {
    return horizontalWelds
  }
  const isDuplicateHorizontalWeld = (
    horiWeld: GeckoComponentRect,
    repairBoxes: GeckoComponentRect[]
  ) =>
    repairBoxes.some(
      (repairBox) =>
        (GeckoMeasurement.Equivalent(repairBox.y.end, horiWeld.y.end) ||
          GeckoMeasurement.Equivalent(repairBox.y.start, horiWeld.y.start)) &&
        GeckoMeasurement.Equivalent(repairBox.x.end, horiWeld.x.end) &&
        GeckoMeasurement.Equivalent(repairBox.x.start, horiWeld.x.start)
    )

  return horizontalWelds.filter(
    (horiWeld) =>
      !isDuplicateHorizontalWeld(horiWeld.feature_geometry, repairRects)
  )
}

export function filterVerticalWelds(
  verticalWelds: GeckoVerticalWeld[],
  repairBoxes: GeckoComponentRect[]
) {
  if (repairBoxes.length === 0) {
    return verticalWelds
  }
  const isDuplicateVerticalWeld = (
    vertWeld: GeckoComponentRect,
    repairBoxes: GeckoComponentRect[]
  ) =>
    repairBoxes.some(
      (repairBox) =>
        (GeckoMeasurement.Equivalent(repairBox.x.end, vertWeld.x.end) ||
          GeckoMeasurement.Equivalent(repairBox.x.start, vertWeld.x.end)) &&
        GeckoMeasurement.Equivalent(repairBox.y.end, vertWeld.y.end) &&
        GeckoMeasurement.Equivalent(repairBox.y.start, vertWeld.y.start)
    )

  return verticalWelds.filter(
    (vertWeld) =>
      !isDuplicateVerticalWeld(vertWeld.feature_geometry, repairBoxes)
  )
}

/**
 *
 * @param shellCourseNominal must be in inches
 * @returns
 */
export function calculateMinimumHorizontalWeldSpacingForBottomPlate(
  shellCourseNominal: number
) {
  // Governed by API 653
  const valInInches =
    shellCourseNominal <= 0.5 ? 6 : max([10, 8 * shellCourseNominal])
  return GeckoMeasurement.CreateFromValue(
    valInInches,
    GeckoMeasurementUnit.Inches
  )
}

/**
 *
 *
 * @param shellCourseNominal must be in inches
 * @return
 */
export function calculateMinimumVerticalWeldSpacing(
  shellCourseNominal: number
) {
  // Governed by API 653
  const valInInches =
    shellCourseNominal <= 0.5 ? 6 : max([10, 8 * shellCourseNominal])
  return GeckoMeasurement.CreateFromValue(
    valInInches,
    GeckoMeasurementUnit.Inches
  )
}

/**
 *
 * @param shellCourseNominal must be in inches
 * @returns
 */
export function calculateMinimumHorizontalWeldSpacing(
  shellCourseNominal: number
) {
  // Governed by API 653
  const valInInches =
    shellCourseNominal <= 0.5 ? 3 : max([10, 8 * shellCourseNominal])
  return GeckoMeasurement.CreateFromValue(
    valInInches,
    GeckoMeasurementUnit.Inches
  )
}

/**
 *
 * @param shellCourseNominal must be inches
 * @returns
 */
export function calculateMinimumRepairDim(
  shellCourseNominal: number | undefined
): GeckoMeasurement | undefined {
  if (!shellCourseNominal) {
    return undefined
  }
  const valInInches =
    shellCourseNominal < 0.5 ? 12 : max([12, 12 * shellCourseNominal])
  return GeckoMeasurement.CreateFromValue(
    valInInches,
    GeckoMeasurementUnit.Inches
  )
}

/**
 *
 * @param shellCourseNominal must be in inches
 * @returns
 */
export function calculateMinimumRepairWidth(
  shellCourseNominal: number | undefined
) {
  if (shellCourseNominal === undefined) {
    return undefined
  }
  const valInInches =
    shellCourseNominal <= 0.5
      ? // Convert from inches to feet
        12
      : max([12, 12 * shellCourseNominal])
  return GeckoMeasurement.CreateFromValue(
    valInInches,
    GeckoMeasurementUnit.Inches
  )
}

/**
 *
 * @param shellCourseNominal must be in inches
 * @returns
 */
export function calculateMinimumRepairHeight(
  shellCourseNominal: number | undefined
) {
  if (shellCourseNominal === undefined) {
    return undefined
  }
  const valInInches =
    shellCourseNominal <= 0.5
      ? // Convert from inches to feet
        12
      : max([12, 12 * shellCourseNominal])
  return GeckoMeasurement.CreateFromValue(
    valInInches,
    GeckoMeasurementUnit.Inches
  )
}

export function passesBottomWeldPlateDispositionForYStart(
  yStart: GeckoMeasurement | undefined,
  bottomShellCourseNominal: number | undefined
): boolean | undefined {
  //TODO there's nuance around radius for the case when the shell meets the bottom (tombstone design)
  if (undefinedTypeCheck([yStart, bottomShellCourseNominal])) {
    return undefined
  }

  const yStartInFt = yStart?.convertTo(GeckoMeasurementUnit.Feet).num

  if (yStartInFt! < 0) {
    throw Error('Y Start cannot be less than 0')
  }
  return (
    yStartInFt === 0 ||
    round(yStartInFt!, 3) >=
      round(
        calculateMinimumHorizontalWeldSpacingForBottomPlate(
          bottomShellCourseNominal!
        ).convertTo(GeckoMeasurementUnit.Feet).num,
        3
      )
  )
}

function hasSCHeightException(
  repair: GeckoComponentRect | undefined,
  side: Side,
  weldPosition: GeckoMeasurement,
  scHeight: GeckoMeasurement
) {
  if (!repair) {
    return
  }

  if (side.includes('y')) {
    return
  }

  const repairX = side === 'x_start' ? repair.x.start : repair.x.end

  // If two repairs are at the same position, and have the height of the shell course (technically shell plate) then they are compliant
  return (
    Math.abs(
      weldPosition.minus(repairX).toNumber(GeckoMeasurementUnit.Inches)
    ) <= 0.5 &&
    Math.abs(
      scHeight.minus(repair.y.size).toNumber(GeckoMeasurementUnit.Inches)
    ) <= 0.5
  )
}

export function passesWeldDisposition(
  repair: GeckoComponentRect | undefined,
  side: Side,
  weldPosition: GeckoMeasurement | undefined,
  shellCourseNominal: number | undefined,
  shellCourseHeight: GeckoMeasurement | undefined
) {
  if (!repair || !weldPosition || !shellCourseHeight || !shellCourseHeight) {
    return undefined
  }

  switch (side) {
    case 'x_start':
      return (
        hasSCHeightException(repair, side, weldPosition, shellCourseHeight) ||
        passesVerticalWeldDisposition(
          repair.x.start,
          weldPosition,
          shellCourseNominal
        )
      )
    case 'x_end':
      return (
        hasSCHeightException(repair, side, weldPosition, shellCourseHeight) ||
        passesVerticalWeldDisposition(
          repair.x.end,
          weldPosition,
          shellCourseNominal
        )
      )
    case 'y_start':
      return passesHorizontalWeldDisposition(
        repair.y.start,
        weldPosition,
        shellCourseNominal
      )
    case 'y_end':
      return passesHorizontalWeldDisposition(
        repair.y.end,
        weldPosition,
        shellCourseNominal
      )
  }
}

export function passesVerticalWeldDisposition(
  xPosition: GeckoMeasurement | undefined,
  weldXPosition: GeckoMeasurement | undefined,
  shellCourseNominal: number | undefined
) {
  if (undefinedTypeCheck([xPosition, shellCourseNominal, weldXPosition])) {
    return undefined
  }

  const xPositionInFt = xPosition?.convertTo(GeckoMeasurementUnit.Feet).num
  const weldXPositionInFt = weldXPosition?.convertTo(
    GeckoMeasurementUnit.Feet
  ).num

  //TODO Think about how we're going to manage wrapped markup
  //if (xPositionInFt! < 0 || weldXPositionInFt! < 0) {
  //  throw Error('X Start cannot be less than 0')
  //}
  return (
    round(abs(weldXPositionInFt! - xPositionInFt!), 3) >=
    round(
      calculateMinimumVerticalWeldSpacing(shellCourseNominal!).convertTo(
        GeckoMeasurementUnit.Feet
      ).num,
      3
    )
  )
}

export function passesHorizontalWeldDisposition(
  yPosition: GeckoMeasurement | undefined,
  weldYPosition: GeckoMeasurement | undefined,
  shellCourseNominal: number | undefined
) {
  if (undefinedTypeCheck([yPosition, shellCourseNominal, weldYPosition])) {
    return undefined
  }

  const yPositionInFt = yPosition?.convertTo(GeckoMeasurementUnit.Feet).num
  const weldYPositionInFt = weldYPosition?.convertTo(
    GeckoMeasurementUnit.Feet
  ).num
  return (
    round(abs(weldYPositionInFt! - yPositionInFt!), 3) >=
      round(
        calculateMinimumHorizontalWeldSpacing(shellCourseNominal!).convertTo(
          GeckoMeasurementUnit.Feet
        ).num,
        3
      ) || abs(round(weldYPositionInFt!, 3) - round(yPositionInFt!, 3)) === 0
  )
}

export function passesAPI653(
  repair: GeckoComponentRect,
  nearestWelds: GeckoComponentRect,
  relevantShellCourseNominal: number | undefined,
  shellCourseHeight: GeckoMeasurement | undefined
) {
  return (
    passesMinWidthDisposition(repair, relevantShellCourseNominal) &&
    passesMinHeightDisposition(repair, relevantShellCourseNominal) &&
    passesWeldDisposition(
      repair,
      'x_start',
      nearestWelds?.x.start.convertTo(GeckoMeasurementUnit.Feet),
      relevantShellCourseNominal,
      shellCourseHeight
    ) &&
    passesWeldDisposition(
      repair,
      'x_end',
      nearestWelds?.x.end.convertTo(GeckoMeasurementUnit.Feet),
      relevantShellCourseNominal,
      shellCourseHeight
    ) &&
    passesWeldDisposition(
      repair,
      'y_start',
      nearestWelds?.y.end.convertTo(GeckoMeasurementUnit.Feet),
      relevantShellCourseNominal,
      shellCourseHeight
    ) &&
    passesWeldDisposition(
      repair,
      'y_end',
      nearestWelds?.y.end.convertTo(GeckoMeasurementUnit.Feet),
      relevantShellCourseNominal,
      shellCourseHeight
    ) &&
    passesBottomWeldPlateDispositionForYStart(
      repair?.y.start.convertTo(GeckoMeasurementUnit.Feet),
      relevantShellCourseNominal
    )
  )
}

export function doesWeldOverlapPerAPI653(
  repair: GeckoComponentRect,
  weld: GeckoWeld,
  side: Side
) {
  const weldGeo = weld.feature_geometry
  if (side === 'x_end' || side === 'x_start') {
    return rangeOverlaps(weldGeo.y, repair.y)
  } else if (side === 'y_end' || side === 'y_start') {
    return rangeOverlaps(weldGeo.x, repair.x)
  } else {
    throw Error('Side not selected')
  }
}

export function passesMinWidthDisposition(
  edges: GeckoComponentRect | undefined,
  shellCourseNominal: number | undefined
) {
  if (edges === undefined || shellCourseNominal === undefined) {
    return undefined
  }
  return edges.x.end
    .minus(edges.x.start)
    .gte(calculateMinimumRepairWidth(shellCourseNominal)!)
}

export function passesMinHeightDisposition(
  edges: GeckoComponentRect | undefined,
  shellCourseNominal: number | undefined
) {
  if (edges === undefined || shellCourseNominal === undefined) {
    return undefined
  }
  return edges.y.end
    .minus(edges.y.start)
    .gte(calculateMinimumRepairHeight(shellCourseNominal)!)
}

export function splitBoxesAcrossCourses(
  plates: GeckoComponentRect[],
  courses: readonly GeckoShellCourse[]
) {
  const newBoxes: GeckoComponentRect[] = []
  const courseGeos = courses.map((course) => course.geometry)
  plates.forEach((plate) => {
    newBoxes.push(...splitBoxAcrossBoxes(plate, [...courseGeos], 'y'))
  })
  return boxesPerCourse(newBoxes, courseGeos)
}

export function splitBoxAcrossBoxes(
  rect: GeckoComponentRect,
  otherRects: GeckoComponentRect[],
  side: 'x' | 'y'
) {
  const newBoxes: GeckoComponentRect[] = []
  const sortedOtherRects = [
    ...otherRects.sort((a, b) => {
      return a.start[side].gte(b.start[side]) ? 1 : -1
    }),
  ]

  const checkBox = sortedOtherRects.pop()
  if (checkBox) {
    if (boxAcrossLine(rect, checkBox.end[side], side)) {
      const belowBox = modifyGCRect(rect, `${side}_end`, checkBox.end[side])
      const aboveBox = modifyGCRect(rect, `${side}_start`, checkBox.end[side])
      newBoxes.push(
        ...splitBoxAcrossBoxes(belowBox!, sortedOtherRects, side),
        ...splitBoxAcrossBoxes(aboveBox!, sortedOtherRects, side)
      )
    } else {
      newBoxes.push(...splitBoxAcrossBoxes(rect, sortedOtherRects, side))
    }
    return newBoxes
  } else {
    return [rect]
  }
}

function boxAcrossLine(
  box: GeckoComponentRect,
  line: GeckoMeasurement,
  side: 'x' | 'y'
) {
  return box.end[side].gt(line) && box.start[side].lt(line)
}

function boxesPerCourse(
  boxes: GeckoComponentRect[],
  courses: GeckoComponentRect[]
) {
  return courses.map((course, index) => {
    const prevCourse =
      index > 0
        ? courses[index - 1].y.end
        : GeckoMeasurement.Zero(course.y.end.unit)
    return boxes.filter((box) => {
      return box.y.end.lte(course.y.end) && box.y.start.gte(prevCourse)
    })
  })
}

export function withinCheck(
  potential1: GeckoMeasurement,
  potential2: GeckoMeasurement,
  side1: GeckoMeasurement,
  side2: GeckoMeasurement
) {
  const potential1Feet = potential1.convertTo(GeckoMeasurementUnit.Feet).num
  const potential2Feet = potential2.convertTo(GeckoMeasurementUnit.Feet).num
  const side1Feet = side1.convertTo(GeckoMeasurementUnit.Feet).num
  const side2Feet = side2.convertTo(GeckoMeasurementUnit.Feet).num

  return withinCheckDirect(potential1Feet, potential2Feet, side1Feet, side2Feet)
}

export function withinCheckDirect(
  potential1: number,
  potential2: number,
  side1: number,
  side2: number
) {
  return (
    (potential1 >= side1 && potential1 <= side2) ||
    (potential2 >= side1 && potential2 <= side2) ||
    (side1 >= potential1 && side1 <= potential2) ||
    (side2 >= potential1 && side2 <= potential2)
  )
}
